<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAddressFilter from './PostSaleAddressFilter.vue';
import PostSaleClientsFilter from './PostSaleClientsFilter.vue';
import PostSaleCasesFilters from './PostSaleCasesFilters.vue';
import PostSaleCasesTablePagination from './PostSaleCasesTablePagination.vue';
import PostSaleCasesTableRows from './PostSaleCasesTableRows.vue';
import SalesSort from './SalesSort.vue';

const props = defineProps({
  activeList: { type: String, required: true },
  addressOptions: { type: Array, default: () => [] },
  addressesLoading: { type: Boolean, default: false },
  agents: { type: Array, default: () => [] },
  cases: { type: Array, default: () => [] },
  clientOptions: { type: Array, default: () => [] },
  clientsLoading: { type: Boolean, default: false },
  counts: { type: Object, required: true },
  error: { type: String, default: '' },
  filters: { type: Object, required: true },
  loading: { type: Boolean, default: false },
  page: { type: Number, required: true },
  sort: { type: String, default: '' },
  totalPages: { type: Number, required: true },
  totalRows: { type: Number, required: true },
});

const emit = defineEmits([
  'applyFilters',
  'changePage',
  'clearFilters',
  'open',
  'applySort',
  'action',
  'selectList',
  'searchAddresses',
]);

const { t } = useI18n();
const filterOpen = ref(false);

const tabs = computed(() => [
  {
    id: 'andamento',
    label: t('POST_SALE.LISTS.IN_PROGRESS'),
    badge: props.counts.andamento,
  },
  {
    id: 'revisoes',
    label: t('POST_SALE.LISTS.REVIEW'),
    badge: props.counts.revisoes,
  },
  {
    id: 'finalizadas',
    label: t('POST_SALE.LISTS.COMPLETED'),
    badge: props.counts.finalizadas,
  },
  {
    id: 'cancelados',
    label: t('POST_SALE.LISTS.CANCELED'),
    badge: props.counts.cancelados,
  },
]);

const activeFilterCount = computed(
  () =>
    Object.values(props.filters).filter(value =>
      Array.isArray(value) ? value.length : Boolean(value)
    ).length
);
const sortVisible = computed(() =>
  ['andamento', 'revisoes'].includes(props.activeList)
);

const applyFilters = filters => {
  filterOpen.value = false;
  emit('applyFilters', filters);
};
const applyClients = clients =>
  emit('applyFilters', { ...props.filters, clients });
const applyAddress = address =>
  emit('applyFilters', { ...props.filters, ...address });
</script>

<template>
  <div class="flex min-w-0 flex-col gap-4 p-4 lg:p-6">
    <div
      role="tablist"
      :aria-label="t('POST_SALE.ARIA.LISTS')"
      class="flex h-14 items-end gap-2 overflow-x-auto border-b border-lais-border-soft [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeList === tab.id ? 'true' : 'false'"
        class="h-14 shrink-0 border-b-2 px-4 text-sm font-bold transition"
        :class="
          activeList === tab.id
            ? 'border-[#01988C] text-[#01988C]'
            : 'border-transparent text-lais-text-body hover:text-[#01988C]'
        "
        @click="emit('selectList', tab.id)"
      >
        {{ tab.label }}
        <span v-if="tab.badge" class="ml-1">
          {{ `(${tab.badge})` }}
        </span>
      </button>
    </div>

    <div class="grid min-w-0 gap-4 md:grid-cols-2">
      <PostSaleAddressFilter
        :filters="filters"
        :loading="addressesLoading"
        :options="addressOptions"
        @apply="applyAddress"
        @search="emit('searchAddresses', $event)"
      />

      <PostSaleClientsFilter
        compact
        :loading="clientsLoading"
        :model-value="filters.clients || []"
        :options="clientOptions"
        @update:model-value="applyClients"
      />
    </div>

    <div
      class="overflow-hidden rounded-[12px] border-2 border-lais-border-soft bg-white shadow-lais-card"
    >
      <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-2">
        <div class="flex flex-wrap items-center gap-2">
          <LaisPrimaryButton
            icon="i-lucide-list-filter"
            variant="ghost"
            size="sm"
            @click="filterOpen = !filterOpen"
          >
            {{ t('POST_SALE.ACTIONS.FILTER') }}
            <span v-if="activeFilterCount">
              {{ t('POST_SALE.FILTERS.COUNT', { count: activeFilterCount }) }}
            </span>
          </LaisPrimaryButton>
          <SalesSort
            v-if="sortVisible"
            :active-list="activeList"
            :sort="sort"
            @change="emit('applySort', $event)"
          />
        </div>
        <PostSaleCasesTablePagination
          :page="page"
          :total-pages="totalPages"
          :total-rows="totalRows"
          :visible-rows="cases.length"
          @change-page="emit('changePage', $event)"
        />
      </div>

      <PostSaleCasesFilters
        v-if="filterOpen"
        class="mx-4 mb-4"
        :agents="agents"
        :filters="filters"
        @apply="applyFilters"
        @clear="emit('clearFilters')"
      />

      <p
        v-if="error"
        class="mx-4 mb-4 rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ error }}
      </p>

      <PostSaleCasesTableRows
        :active-list="activeList"
        :cases="cases"
        :loading="loading"
        @action="emit('action', $event)"
        @open="emit('open', $event)"
      />

      <PostSaleCasesTablePagination
        footer
        :page="page"
        :total-pages="totalPages"
        :total-rows="totalRows"
        :visible-rows="cases.length"
        @change-page="emit('changePage', $event)"
      />
    </div>
  </div>
</template>
